import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Divider,
  Alert,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Visibility, Warning, MonitorHeart } from "@mui/icons-material";
import ComprehensiveProfile from "../Profile/ComprehensiveProfile";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";

const TicketView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const { ticket: stateTicket, patient: statePatient } = location.state || {};

  const [showFullProfile, setShowFullProfile] = useState(false);
  const auth = useAuth();
  const token = auth?.user?.token;

  // Fetch ticket if not in state
  const { data: ticketData } = useFetchObjects(
    ticketId && !stateTicket ? ["ticket", ticketId] : null,
    ticketId && !stateTicket ? `tickets/${ticketId}/` : "",
    token
  );

  const ticket = stateTicket || ticketData;
  
  // Extract patient ID - handle both cases: patient as ID or patient as object
  let patientId = null;
  let patientData = null;
  
  if (statePatient) {
    const raw = statePatient?.results?.[0] || statePatient;
    // Only use as patientData if it's an object with id/name, not just an ID
    if (raw && typeof raw === "object" && !Number.isInteger(raw)) {
      patientData = raw;
      patientId = patientData?.id ?? patientId;
    } else if (raw != null) {
      patientId = typeof raw === "object" ? raw?.id : raw;
    }
  }
  if ((patientId == null || patientData == null) && ticket?.patient) {
    if (typeof ticket.patient === "number" || typeof ticket.patient === "string") {
      patientId = ticket.patient;
    } else if (ticket.patient && typeof ticket.patient === "object" && (ticket.patient.id != null || ticket.patient.firstname != null || ticket.patient.first_name != null)) {
      patientId = ticket.patient.id ?? patientId;
      patientData = ticket.patient;
    } else {
      patientId = ticket.patient;
    }
  }

  // Fetch patient data if we only have ID
  const { data: fetchedPatientData } = useFetchObjects(
    patientId && !patientData ? ["patient", patientId] : null,
    patientId && !patientData ? `patients/${patientId}/` : "",
    token
  );

  // Fetch recent vital signs and encounters for this ticket/patient
  const { data: ehrData } = useFetchObjects(
    patientId ? ["ticket-ehr", patientId, ticket?.id] : null,
    patientId ? `patients/${patientId}/unified-ehr/${ticket?.id ? `?ticket_id=${ticket.id}` : ""}` : "",
    token
  );

  // Get most recent vital signs
  const latestVitalSigns = useMemo(() => {
    if (!ehrData?.timeline) return null;
    const vitalSigns = ehrData.timeline
      .filter((item) => item.type === "vital_signs")
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    return vitalSigns.length > 0 ? vitalSigns[0] : null;
  }, [ehrData]);

  // Get patient complaints/issues from encounters
  const patientComplaints = useMemo(() => {
    if (!ehrData?.timeline) return [];
    return ehrData.timeline
      .filter((item) => item.type === "encounter" && (item.data?.chief_complaint || item.data?.diagnosis))
      .map((item) => ({
        date: item.date,
        chief_complaint: item.data?.chief_complaint,
        diagnosis: item.data?.diagnosis,
        encounter_type: item.data?.encounter_type_display,
      }))
      .slice(0, 3); // Show last 3
  }, [ehrData]);

  // Check if vital signs are critical
  const isCritical = useMemo(() => {
    if (!latestVitalSigns?.data) return false;
    const vs = latestVitalSigns.data;
    return (
      (vs.temperature != null && (vs.temperature < 36 || vs.temperature > 38.5)) ||
      (vs.blood_pressure_systolic != null && (vs.blood_pressure_systolic < 90 || vs.blood_pressure_systolic > 160)) ||
      (vs.blood_pressure_diastolic != null && (vs.blood_pressure_diastolic < 60 || vs.blood_pressure_diastolic > 100)) ||
      (vs.heart_rate != null && (vs.heart_rate < 60 || vs.heart_rate > 100)) ||
      (vs.respiratory_rate != null && (vs.respiratory_rate < 12 || vs.respiratory_rate > 20)) ||
      (vs.oxygen_saturation != null && vs.oxygen_saturation < 95) ||
      (vs.pain_score != null && vs.pain_score >= 7)
    );
  }, [latestVitalSigns]);

  // Use fetched patient data if available (API may return single object or { results: [...] })
  if (fetchedPatientData && !patientData) {
    patientData = fetchedPatientData?.results?.[0] || (Array.isArray(fetchedPatientData) ? fetchedPatientData[0] : fetchedPatientData);
  }

  // When we only have patientId, build patientData from ticket display fields (backend returns patient_name_display etc.)
  if (patientId && ticket && !patientData && (ticket.patient_name_display != null || ticket.patient_phone_display != null)) {
    const fullName = ticket.patient_name_display || "";
    const parts = fullName.trim().split(/\s+/);
    patientData = {
      id: patientId,
      firstname: parts[0] || fullName,
      lastname: parts.slice(1).join(" ") || "",
      first_name: parts[0] || fullName,
      last_name: parts.slice(1).join(" ") || "",
      phone: ticket.patient_phone_display,
      age: ticket.patient_age_display,
      gender: ticket.patient_gender_display,
      _displayName: fullName,
    };
  }
  // When we have patientData but ticket has display name, prefer ticket name for display (in case fetch returned different format)
  if (patientId && ticket?.patient_name_display && patientData) {
    patientData = { ...patientData, _displayName: ticket.patient_name_display };
  }

  if (!ticket || !patientId) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          {t("profile.ticketNotFound") || "Ticket not found"}
        </Typography>
      </Box>
    );
  }

  // Ensure patientId is a number/string, not an object
  if (typeof patientId === 'object' && patientId?.id) {
    patientId = patientId.id;
  }

  if (showFullProfile) {
    return (
      <ComprehensiveProfile
        patientId={patientId}
        ticketId={ticket.id}
        showFullHistory={true}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
      {/* Ticket Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom>
                {t("profile.ticket") || "Ticket"}: {ticket.ticket_number}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                <Chip
                  label={ticket.status_display || "Active"}
                  color={ticket.status === 1 ? "success" : ticket.status === 2 ? "default" : "error"}
                  size="small"
                />
                {ticket.department_name_display && (
                  <Chip
                    label={ticket.department_name_display}
                    variant="outlined"
                    size="small"
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {t("profile.visitDate") || "Visit Date"}:{" "}
                  {new Date(ticket.visit_date).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Button
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => setShowFullProfile(true)}
              >
                {t("profile.seeFullProfile") || "See Full Profile"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Patient Info and Vital Signs Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Patient Info Card */}
        <Grid item xs={12} md={patientComplaints.length > 0 || latestVitalSigns ? 6 : 12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("profile.patientInformation") || "Patient Information"}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {patientData ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("profile.patientName") || "Name"}
                      </Typography>
                      <Typography variant="body1">
                        {patientData._displayName || [patientData.firstname || patientData.first_name, patientData.lastname || patientData.last_name].filter(Boolean).join(" ") || t("profile.noName") || "—"}
                      </Typography>
                    </Grid>
                    {patientData.phone && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.phone") || "Phone"}
                        </Typography>
                        <Typography variant="body1">{patientData.phone}</Typography>
                      </Grid>
                    )}
                    {patientData.age && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.age") || "Age"}
                        </Typography>
                        <Typography variant="body1">{patientData.age}</Typography>
                      </Grid>
                    )}
                    {patientData.gender && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.gender") || "Gender"}
                        </Typography>
                        <Typography variant="body1">
                          {patientData.gender === "1" || patientData.gender === 1
                            ? t("profile.male")
                            : t("profile.female")}
                        </Typography>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t("profile.loadingPatientInfo") || "Loading patient information..."}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Latest Vital Signs Card */}
        {latestVitalSigns && (
          <Grid item xs={12} md={patientComplaints.length > 0 ? 3 : 6}>
            <Card sx={{ border: isCritical ? "2px solid" : "1px solid", borderColor: isCritical ? "error.main" : "divider" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <MonitorHeart color={isCritical ? "error" : "primary"} />
                  <Typography variant="h6">
                    {t("profile.latestVitalSigns") || "Latest Vital Signs"}
                  </Typography>
                  {isCritical && (
                    <Chip
                      icon={<Warning />}
                      label={t("profile.critical") || "Critical"}
                      color="error"
                      size="small"
                      sx={{ ml: "auto" }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {new Date(latestVitalSigns.date).toLocaleString()}
                  {latestVitalSigns.data?.recorded_by && ` · ${latestVitalSigns.data.recorded_by}`}
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {latestVitalSigns.data?.temperature != null && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("profile.temperature") || "Temp"}</Typography>
                      <Typography variant="body2" color={latestVitalSigns.data.temperature < 36 || latestVitalSigns.data.temperature > 38.5 ? "error.main" : "text.primary"}>
                        {latestVitalSigns.data.temperature} °C
                      </Typography>
                    </Grid>
                  )}
                  {(latestVitalSigns.data?.blood_pressure_systolic != null || latestVitalSigns.data?.blood_pressure_diastolic != null) && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("profile.bloodPressure") || "BP"}</Typography>
                      <Typography variant="body2" color={
                        (latestVitalSigns.data.blood_pressure_systolic != null && (latestVitalSigns.data.blood_pressure_systolic < 90 || latestVitalSigns.data.blood_pressure_systolic > 160)) ||
                        (latestVitalSigns.data.blood_pressure_diastolic != null && (latestVitalSigns.data.blood_pressure_diastolic < 60 || latestVitalSigns.data.blood_pressure_diastolic > 100))
                          ? "error.main" : "text.primary"
                      }>
                        {latestVitalSigns.data.blood_pressure_systolic ?? "—"}/{latestVitalSigns.data.blood_pressure_diastolic ?? "—"} mmHg
                      </Typography>
                    </Grid>
                  )}
                  {latestVitalSigns.data?.heart_rate != null && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("profile.heartRate") || "HR"}</Typography>
                      <Typography variant="body2" color={latestVitalSigns.data.heart_rate < 60 || latestVitalSigns.data.heart_rate > 100 ? "error.main" : "text.primary"}>
                        {latestVitalSigns.data.heart_rate} bpm
                      </Typography>
                    </Grid>
                  )}
                  {latestVitalSigns.data?.oxygen_saturation != null && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("profile.spO2") || "SpO2"}</Typography>
                      <Typography variant="body2" color={latestVitalSigns.data.oxygen_saturation < 95 ? "error.main" : "text.primary"}>
                        {latestVitalSigns.data.oxygen_saturation}%
                      </Typography>
                    </Grid>
                  )}
                  {latestVitalSigns.data?.pain_score != null && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("profile.painScore") || "Pain"}</Typography>
                      <Typography variant="body2" color={latestVitalSigns.data.pain_score >= 7 ? "error.main" : "text.primary"}>
                        {latestVitalSigns.data.pain_score}/10
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Patient Complaints/Issues Card */}
        {patientComplaints.length > 0 && (
          <Grid item xs={12} md={latestVitalSigns ? 3 : 6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("profile.complaintsAndIssues") || "Complaints & Issues"}
                </Typography>
                {patientComplaints.map((complaint, idx) => (
                  <Box key={idx} sx={{ mb: 1.5, pb: 1.5, borderBottom: idx < patientComplaints.length - 1 ? "1px solid" : "none", borderColor: "divider" }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(complaint.date).toLocaleDateString()} · {complaint.encounter_type || t("profile.consultation")}
                    </Typography>
                    {complaint.chief_complaint && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>{t("profile.chiefComplaint") || "Complaint"}:</strong> {complaint.chief_complaint}
                      </Typography>
                    )}
                    {complaint.diagnosis && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>{t("profile.diagnosis") || "Diagnosis"}:</strong> {complaint.diagnosis}
                      </Typography>
                    )}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Comprehensive Profile for this ticket */}
      <ComprehensiveProfile
        patientId={patientId}
        ticketId={ticket.id}
        showFullHistory={false}
      />
    </Box>
  );
};

export default TicketView;
